import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, RefreshCw } from 'lucide-react';
import { spApiOauthApi } from '@/api/client';
import { AsinIdentity } from '@/components/retail/AsinIdentity';
import { RetailDataTable } from '@/components/retail/RetailDataTable';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { SignOutButton } from '@/components/auth/SignOutButton';
import { formatCurrency } from '@/utils/formatCurrency';

function formatMoney(amount: number, currency: string | null | undefined): string {
  return formatCurrency(amount, { currencyCode: currency ?? null }, {
    maximumFractionDigits: 2,
  });
}

export function RetailPage() {
  const { credentialId: rawId } = useParams<{ credentialId: string }>();
  const credentialId = rawId ? decodeURIComponent(rawId) : '';
  const [refreshKey, setRefreshKey] = useState(0);
  const refresh = refreshKey > 0;

  const salesQuery = useQuery({
    queryKey: ['sp-api-sales', credentialId, refreshKey],
    queryFn: async () => {
      const res = await spApiOauthApi.getSales(credentialId, {
        refresh,
        period: 'DAY',
      });
      if (!res.success || !res.data) throw new Error('Failed to load sales');
      return res.data;
    },
    enabled: Boolean(credentialId),
    staleTime: 60_000,
  });

  const asinsQuery = useQuery({
    queryKey: ['sp-api-asins', credentialId, refreshKey],
    queryFn: async () => {
      const res = await spApiOauthApi.getAsins(credentialId, {
        refresh,
        period: 'WEEK',
      });
      if (!res.success || !res.data) throw new Error('Failed to load ASINs');
      return res.data;
    },
    enabled: Boolean(credentialId),
    staleTime: 60_000,
  });

  const connection = salesQuery.data?.connection || asinsQuery.data?.connection;
  const summary = salesQuery.data?.summary;
  const asins = asinsQuery.data?.asins ?? [];

  const title = useMemo(() => {
    if (!connection) return 'Retail data';
    const kind = connection.connection_type === 'vendor' ? 'Vendor' : 'Seller';
    return connection.display_name || `${kind} · ${connection.selling_partner_id}`;
  }, [connection]);

  const loading = salesQuery.isLoading || asinsQuery.isLoading;
  const fetching = salesQuery.isFetching || asinsQuery.isFetching;
  const error =
    (salesQuery.error as Error | null)?.message ||
    (asinsQuery.error as Error | null)?.message ||
    null;

  return (
    <div className="p-6 max-w-5xl space-y-8">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <Link
            to="/admin/settings"
            className="inline-flex items-center gap-1.5 text-sm text-foreground-muted hover:text-foreground mb-3"
          >
            <ArrowLeft className="h-3.5 w-3.5" aria-hidden />
            Admin settings
          </Link>
          <h1 className="text-headline-md font-semibold text-foreground">{title}</h1>
          {connection && (
            <p className="text-body-sm text-foreground-muted mt-1">
              {connection.connection_type === 'vendor' ? 'Vendor Central' : 'Seller Central'} ·{' '}
              {connection.region}
              {connection.marketplace_id ? ` · ${connection.marketplace_id}` : ''}
            </p>
          )}
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          <Button
            type="button"
            variant="secondary"
            disabled={fetching || !credentialId}
            onClick={() => setRefreshKey((k) => k + 1)}
          >
            {fetching ? (
              <Spinner className="h-3.5 w-3.5 mr-1.5" />
            ) : (
              <RefreshCw className="h-3.5 w-3.5 mr-1.5" aria-hidden />
            )}
            Refresh from Amazon
          </Button>
          <SignOutButton />
        </div>
      </div>

      {loading && !salesQuery.data && !asinsQuery.data ? (
        <div className="flex justify-center py-16">
          <Spinner />
        </div>
      ) : error && !salesQuery.data && !asinsQuery.data ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          {error}
          <p className="mt-2 text-red-700/80">
            Reports can take a few minutes. Try refresh again, or reconnect in Admin settings if
            authorization expired.
          </p>
        </div>
      ) : (
        <>
          <section className="space-y-3">
            <div className="flex items-baseline justify-between gap-3">
              <h2 className="text-lg font-semibold text-foreground">Sales</h2>
              {salesQuery.data?.fetched_at && (
                <p className="text-xs text-foreground-muted">
                  {salesQuery.data.cached ? 'Cached' : 'Live'} ·{' '}
                  {new Date(salesQuery.data.fetched_at).toLocaleString()}
                  {salesQuery.data.start && salesQuery.data.end
                    ? ` · ${salesQuery.data.start} → ${salesQuery.data.end}`
                    : ''}
                </p>
              )}
            </div>
            {salesQuery.isError ? (
              <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
                {(salesQuery.error as Error).message}
              </div>
            ) : summary ? (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Metric
                  label="Ordered revenue"
                  value={formatMoney(summary.ordered_revenue ?? 0, summary.currency)}
                />
                <Metric
                  label="Ordered units"
                  value={(summary.ordered_units ?? 0).toLocaleString()}
                />
                <Metric
                  label="Shipped units"
                  value={
                    summary.shipped_units != null
                      ? summary.shipped_units.toLocaleString()
                      : '—'
                  }
                />
                <Metric
                  label="Returns"
                  value={
                    summary.customer_returns != null
                      ? summary.customer_returns.toLocaleString()
                      : '—'
                  }
                />
              </div>
            ) : null}
          </section>

          <section className="space-y-3">
            <div className="flex items-baseline justify-between gap-3">
              <h2 className="text-lg font-semibold text-foreground">
                ASINs{asinsQuery.data ? ` (${asinsQuery.data.count})` : ''}
              </h2>
              {asinsQuery.data?.fetched_at && (
                <p className="text-xs text-foreground-muted">
                  {asinsQuery.data.cached ? 'Cached' : 'Live'} ·{' '}
                  {new Date(asinsQuery.data.fetched_at).toLocaleString()}
                </p>
              )}
            </div>
            {asinsQuery.isError ? (
              <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
                {(asinsQuery.error as Error).message}
              </div>
            ) : asins.length === 0 ? (
              <div className="rounded-lg border border-gray-200 bg-white p-6 text-center text-sm text-gray-600">
                No ASINs returned for this account.
              </div>
            ) : (
              <RetailDataTable
                empty="No ASINs returned for this account."
                columns={[
                  { key: 'asin', label: 'ASIN', identity: true },
                  { key: 'sellable', label: 'Sellable on hand', align: 'right' },
                  { key: 'openPo', label: 'Open PO units', align: 'right' },
                ]}
                rows={asins.map((row) => ({
                  asin: <AsinIdentity asin={row.asin} title={row.title} imageUrl={row.imageUrl} />,
                  sellable:
                    row.sellableOnHandInventoryUnits != null
                      ? row.sellableOnHandInventoryUnits.toLocaleString()
                      : '—',
                  openPo:
                    row.openPurchaseOrderUnits != null
                      ? row.openPurchaseOrderUnits.toLocaleString()
                      : '—',
                }))}
              />
            )}
          </section>
        </>
      )}
    </div>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
      <p className="text-xs text-gray-500">{label}</p>
      <p className="mt-1 text-lg font-semibold tabular-nums text-gray-900">{value}</p>
    </div>
  );
}
